import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BUSINESS_PARTNER_PERMISSIONS } from 'app/common/constants/permission';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { HasPermissionPipe, PermissionDirective, PermissionService } from 'app/core/permission';
import { ExportDialogComponent } from 'app/layout/export-overlay/dialog/export-dialog.component';
import { ExportOptions } from 'app/layout/export-overlay/tasks/core/export-task.interfaces';
import { MapModalComponent } from 'app/shared/components/map-modal/map-modal.component';
import {
  MapModalTypeEnum,
  MonitorDocumentTypeEnum,
} from 'app/shared/components/map-modal/models/map-modal.enum';
import { MapModalInputData } from 'app/shared/components/map-modal/models/map-modal.interface';
import { AssignedPartnersListComponent } from '../assigned-partners-list/assigned-partners-list.component';
import { AssignedPartnersListService } from '../assigned-partners-list/assigned-partners-list.service';
import { assignedPartnersExportTitle } from '../assigned-partners-list/exports/assigned-partners-list.constants';
import { AssignedPartnersListExport } from '../assigned-partners-list/exports/assigned-partners-list.export';
import { BpGroupingSelectorComponent } from '../components/bp-grouping-selector/bp-grouping-selector.component';
import { PartnerToolbarComponent } from '../components/partner-toolbar/partner-toolbar.component';
import { ToolbarExportButtonComponent } from '../components/toolbar-export-button/toolbar-export-button.component';
import { ToolbarSearchComponent } from '../components/toolbar-search/toolbar-search.component';
import { ToolbarTitleComponent } from '../components/toolbar-title/toolbar-title.component';
import { AppointmentDialogComponent } from '../dialogs/appointment-dialog/appointment-dialog.component';
import { BulkGpsUpdateDialogComponent } from '../dialogs/bulk-gps-update-dialog/bulk-gps-update-dialog.component';
import { FrequencyDialogComponent } from '../dialogs/frequency-dialog/frequency-dialog.component';
import { SubRoutePartnersLayoutService } from '../sub-route-partners-layout.service';
import { BPGroupingCriteria } from '../types/bp-grouping.types';
import { AssignedPartnersGroupedComponent } from './assigned-partners-grouped/assigned-partners-grouped.component';
import { AssignedPartnersPanelService } from './assigned-partners-panel.service';

@Component({
  selector: 'app-assigned-partners-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    AssignedPartnersListComponent,
    AssignedPartnersGroupedComponent,
    PartnerToolbarComponent,
    ToolbarTitleComponent,
    ToolbarSearchComponent,
    ToolbarExportButtonComponent,
    BpGroupingSelectorComponent,
    PermissionDirective,
    HasPermissionPipe,
    TranslatePipe,
  ],
  providers: [
    AssignedPartnersPanelService,
    AssignedPartnersListService,
    AssignedPartnersListExport,
  ],
  templateUrl: './assigned-partners-panel.component.html',
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignedPartnersPanelComponent {
  private dialog = inject(MatDialog);
  private panelService = inject(AssignedPartnersPanelService);
  private listService = inject(AssignedPartnersListService);
  private layoutService = inject(SubRoutePartnersLayoutService);
  private assignedPartnersExport = inject(AssignedPartnersListExport);
  private i18n = inject(LanguageService);
  private permissionService = inject(PermissionService);

  constructor() {
    // Refresh list when partners are assigned from unassigned sidebar
    this.layoutService.refreshAssignedPartners$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.listService.reload$.next();
    });
  }

  // Expose signals for template
  readonly searchTerm = this.panelService.searchTerm;
  readonly viewMode = this.panelService.viewMode;
  readonly groupingCriteria = this.panelService.groupingCriteria;
  readonly routeName = computed(() => this.panelService.subRouteData()?.name ?? '');
  readonly title = computed(() =>
    this.i18n.t('routing.SUB_ROUTE_PARTNERS.PANEL.TITLE', { routeName: this.routeName() })
  );

  // Mobile search visibility
  readonly showMobileSearch = signal(false);

  readonly BUSINESS_PARTNER_PERMISSIONS = BUSINESS_PARTNER_PERMISSIONS;

  // For export
  readonly totalItems = this.listService.totalItems;
  readonly pagination = this.listService.pagination;
  readonly pageIndex = computed(() => (this.pagination().currentPage ?? 1) - 1);

  toggleMobileSearch(): void {
    this.showMobileSearch.update((v) => !v);
  }

  onGroupingChange(criteria: BPGroupingCriteria): void {
    this.panelService.setGroupingCriteria$.next(criteria);
  }

  onExport(): void {
    if (!this.permissionService.has(BUSINESS_PARTNER_PERMISSIONS.EXPORT)) return;
    if (!this.assignedPartnersExport.canStartTask()) return;

    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: { title: assignedPartnersExportTitle() },
    });

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.assignedPartnersExport.export(
          options.format,
          options.range,
          this.totalItems(),
          this.pagination().limitPerPage ?? 50,
          this.pageIndex()
        );
      }
    });
  }

  openMapModal(): void {
    const subRouteData = this.panelService.subRouteData();
    if (!subRouteData) return;

    const data: MapModalInputData = {
      type: MapModalTypeEnum.FRECUENCY_ROUTE,
      tripId: null,
      element: { ...subRouteData, type: 'A' },
      document: MonitorDocumentTypeEnum.NONE,
      groupDocuments: [],
      map: {
        displayMode: 'drawer',
        captions: { title: subRouteData.name || this.i18n.t('routing.SUB_ROUTE_PARTNERS.LIST.EXPORT.TITLE') },
      },
    };

    this.dialog.open(MapModalComponent, { width: '90%', data });
  }

  openFrequencyModal(): void {
    const subRouteData = this.panelService.subRouteData();
    if (!subRouteData) return;

    this.dialog.open(FrequencyDialogComponent, {
      width: '95vw',
      maxHeight: '90vh',
      data: subRouteData,
    });
  }

  openAppointmentModal(): void {
    const subRouteData = this.panelService.subRouteData();
    if (!subRouteData) return;

    this.dialog.open(AppointmentDialogComponent, {
      width: '90%',
      maxHeight: '90vh',
      data: subRouteData,
    });
  }

  openBulkGpsUpdateModal(): void {
    const subRouteData = this.panelService.subRouteData();
    const subRouteId = this.panelService.subRouteIdForListView();
    if (!subRouteData || !subRouteId) return;

    const dialogRef = this.dialog.open(BulkGpsUpdateDialogComponent, {
      width: '95vw',
      maxWidth: '1200px',
      maxHeight: '90vh',
      panelClass: 'responsive-modal',
      disableClose: true,
      data: {
        subRouteId,
        subRouteName: subRouteData.name ?? '',
        companyId: subRouteData.company?._id?.toString() ?? null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.refreshed) {
        this.listService.reload$.next();
      }
    });
  }
}
